import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { ApiResponse, ApiResponseStatusCode, convertToDecimal, Patient, PatientRepository, PatientRequest } from '../core';
import { PatientForm } from '../models';


/**
 * This Service handles patient data.
 */
@Injectable({ providedIn: 'root' })
export class PatientService {

	private readonly patRepo = inject(PatientRepository);

	/**
	* Map patient request from patient form.
	*/
	createPatientRequest(form: PatientForm): PatientRequest {
		return {
			number: form.patientId,
			initials: form.patientInitials,
			age: form.patientAge?.toString() ? Number(form.patientAge) : null,
			gender: form.patientGender,
			weight: form.patientWeightKg == null ? null : convertToDecimal(form.patientWeightKg.toString()),
			heigth: form.patientHeightCm == null ? null : convertToDecimal(form.patientHeightCm.toString()),
		};
	}

	/**
	* Map patient form from patient model.
	*/
	createPatientForm(pat: Patient): PatientForm {
		return {
			patientId: pat.number,
			patientInitials: pat.initials,
			patientAge: pat.age ?? null,
			patientInfant: pat.age == 0,
			patientGender: pat.gender,
			patientWeightKg: pat.weightKG?.toString() ?? null,
			patientWeightLb: null,
			patientHeightCm: pat.heigthCM?.toString() ?? null,
			patientHeightFt: null
		};
	}

	/**
	* Map patient model from patient form.
	*/
	updatePatientModel(pat: Patient, patForm: PatientForm): Patient {
		const patRequest: PatientRequest = this.createPatientRequest(patForm);
		return {
			number: patRequest.number,
			initials: patRequest.initials,
			age: patRequest.age,
			gender: patRequest.gender,
			weightKG: patRequest.weight,
			heigthCM: patRequest.heigth,
			isReceived: pat.isReceived,
			notes: pat.notes,
			id: pat.id,
			userGuid: pat.userGuid
		};
	}

	/**
	* Get patient by patient guid.
	 * @param {string} patGuid Patient guid.
	*/
	getPatient(patGuid: string): Observable<Patient> {
		return this.patRepo.getPatient(patGuid).pipe(
			map(res => this.handlePatientResponse(res))
		);
	}

	/**
	* Save patient by patient request.
	*/
	savePatient(request: PatientRequest): Observable<Patient> {
		return this.patRepo.savePatient(request).pipe(
			map(res => this.handlePatientResponse(res))
		);
	}

	/**
	* Save patient by patient form.
	*/
	savePatientForm(patForm: PatientForm): Observable<Patient> {
		const patRequest: PatientRequest = this.createPatientRequest(patForm);
		return this.savePatient(patRequest);
	}

	/**
	* Update patient with patient form data.
	*/
	updatePatient(pat: Patient, patForm: PatientForm): Observable<any> {
		const patient: Patient = this.updatePatientModel(pat, patForm);
		return this.patRepo.updatePatient(patient).pipe(
			map(res => this.handlePatientResponse(res))
		);
	}

	/**
	* Delete patient by patient guid.
	*/
	deletePatient(patientGuid: string): Observable<any> {
		return this.patRepo.deletePatient(patientGuid);
	}

	private handlePatientResponse(response: ApiResponse<Patient>) {
		switch (response.statusCode) {
			case ApiResponseStatusCode.Success: return response.result as Patient;
			case ApiResponseStatusCode.PatientNumberNotUnique: throw new Error('PatientNumberNotUnique');
			default: throw new Error('Generic error');
		}
	}

}
